import type { FeatureAction, QuantityUnit } from "./feature";

/**
 * SCOPE PACKAGES (Phase 10, spec §21A).
 *
 * A Package is a reusable, named set of scope for a room (or the project) —
 * "Standard Flip Bathroom". It is NOT a second estimating architecture and
 * holds no prices of its own: each item points at the definitions Berrier
 * Reno already has (Feature Definition, Object Definition, Assembly
 * Template, optionally a preferred Product), and applying a package simply
 * creates the ordinary Features, geometry bindings and assemblies that the
 * user could have created by hand.
 *
 * Terminology (deliberate, permanent):
 *  - DEFINITION  — a library entry for ONE thing (a feature, an object).
 *  - TEMPLATE    — an assembly recipe: components, quantities, unit costs.
 *  - PACKAGE     — a library entry naming a SET of scope, expressed with the
 *                  definitions and templates above.
 *  - APPLICATION — the record that a package was applied to a room, which is
 *                  what project scope carries as provenance.
 *
 * One hierarchical model covers "room package", "finish package" and (via
 * `kind: 'project'` plus child-package items) a whole-house standard, so
 * there is no second overlapping template concept.
 */

export type PackageKind = "room" | "project";

/**
 * Optional finish/quality label. Deliberately a LABEL on the package (and
 * on the application it produces), not a taxonomy imposed on products or
 * materials: it exists so future analytics can compare like with like
 * ("Standard bathrooms averaged X") without pretending the app can classify
 * every product in the world.
 */
export type FinishTier = "economy" | "standard" | "premium" | "custom";

export const FINISH_TIERS: ReadonlyArray<{ id: FinishTier; displayName: string }> = [
  { id: "economy", displayName: "Economy" },
  { id: "standard", displayName: "Standard" },
  { id: "premium", displayName: "Premium" },
  { id: "custom", displayName: "Custom" },
];

/** Required items always apply; default-on/off are the reviewable checklist. */
export type PackageInclusion = "required" | "default-on" | "default-off";

export const PACKAGE_INCLUSIONS: ReadonlyArray<{ id: PackageInclusion; displayName: string }> = [
  { id: "required", displayName: "Always included" },
  { id: "default-on", displayName: "Optional — on by default" },
  { id: "default-off", displayName: "Optional — off by default" },
];

/**
 * Where an item's quantity comes from once applied. 'default' defers to the
 * existing rules (geometry binding for room-bound work, object counts for
 * spatial work) rather than inventing a package-specific quantity system.
 */
export type PackageQuantitySource =
  | "default"
  | "room-floor-area"
  | "room-perimeter"
  /** Spec §89: perimeter × wall height (gross wall surface). */
  | "room-wall-area"
  /** Spec §89: a flat ceiling — the floor area, named for what is painted. */
  | "room-ceiling-area"
  | "objects-count"
  | "manual";

export interface ScopePackage {
  id: string;
  orgId: string;
  name: string;
  kind: PackageKind;
  description: string;
  finishTier: FinishTier | null;
  /** Seeded starter package — clearly labeled illustrative, never market truth. */
  isSystem: boolean;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Room types this package is suggested for (never a restriction). */
  roomTypeIds: string[];
  items: ScopePackageItem[];
}

export interface ScopePackageItem {
  id: string;
  packageId: string;
  /** 'feature' = ordinary scope; 'package' = compose another package. */
  itemType: "feature" | "package";
  featureDefinitionId: string | null;
  /** Preferred action; null = the feature definition's own default. */
  action: FeatureAction | null;
  /** Set when this scope is SPATIAL: it lives in the Design as an object. */
  objectDefinitionId: string | null;
  /** How many objects the package expects (guided placement count). */
  objectCount: number;
  /** Default assembly; null = the Phase 8.1 single-compatible-template rule. */
  assemblyTemplateId: string | null;
  quantitySource: PackageQuantitySource;
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  /** Optional exact product preference (never required). */
  preferredProductId: string | null;
  inclusion: PackageInclusion;
  /** For itemType 'package': the composed child package. */
  childPackageId: string | null;
  notes: string;
  sortOrder: number;
}

export interface ScopePackageInput {
  name: string;
  kind: PackageKind;
  description: string;
  finishTier: FinishTier | null;
  roomTypeIds: string[];
}

export interface ScopePackageItemInput {
  itemType: "feature" | "package";
  featureDefinitionId: string | null;
  action: FeatureAction | null;
  objectDefinitionId: string | null;
  objectCount: number;
  assemblyTemplateId: string | null;
  quantitySource: PackageQuantitySource;
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  preferredProductId: string | null;
  inclusion: PackageInclusion;
  childPackageId: string | null;
  notes: string;
}

export function validateScopePackage(input: ScopePackageInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") problems.push("A package needs a name.");
  if (input.kind !== "room" && input.kind !== "project") problems.push("A package is either a room package or a project package.");
  return problems;
}

export function validateScopePackageItem(input: ScopePackageItemInput): string[] {
  const problems: string[] = [];
  if (input.itemType === "feature") {
    if (input.featureDefinitionId === null) problems.push("Choose what scope this item adds.");
    if (input.objectDefinitionId !== null && input.objectCount < 1) {
      problems.push("A spatial item places at least one object.");
    }
  } else {
    if (input.childPackageId === null) problems.push("Choose the package this item includes.");
  }
  if (input.quantitySource === "manual") {
    if (input.quantityX100 === null || !Number.isSafeInteger(input.quantityX100) || input.quantityX100 < 0) {
      problems.push("Enter the fixed quantity for this item.");
    }
    if (input.quantityUnit === null) problems.push("A fixed quantity needs a unit.");
  }
  return problems;
}

/** Spatial items live in the Design; everything else binds to the room. */
export function isSpatialItem(item: Pick<ScopePackageItem, "objectDefinitionId">): boolean {
  return item.objectDefinitionId !== null;
}
